import type { AccountType, Permissions } from '~/types/code/code';
import { joinName } from '~/utils/stringUtils';
import type { Affiliation } from '~/types/common/affiliation';
import type { ProxyLoginMode } from '~/types/common/proxyLoginMode';
import type { StationWithOrganization } from '~/types/common/stationWithOrganization';
import type { WorkTruckInfo } from '~/types/common/workTruckInfo';

type State = {
  /** ログインユーザー情報 */
  loginUser: {
    /** PLAT ID */
    platId: string;
    /** アカウント区分 */
    accountType: AccountType;
    /** ユーザ名 */
    userName: string;
    /** 駅エリア検知フラグ */
    stationAreaDetectionFlag: boolean;
    /** 権限機能リスト */
    permissions: Permissions[];
    /** 所属駅情報 */
    affiliations: Affiliation[];
    /** 作業駅情報 */
    workingStation: StationWithOrganization | undefined;
    /** 選択中のトラック */
    truck: WorkTruckInfo | undefined;
    /** パスワード変更必須フラグ */
    mustChangePassword: boolean;
    /** 代理ログインモード */
    proxyLoginMode?: ProxyLoginMode;
    /** 代理ログイン元ユーザPLAT ID */
    originalPlatId?: string;
    /** 申請中かどうか */
    isInApplication?: boolean;
  } | undefined;
};

/**
 * ログインユーザー情報を管理する
 */
export const useLoginUserStore = defineStore('loginUser', {
  state: (): State => ({
    loginUser: undefined,
  }),
  // Stateをセッションストレージに保存する
  persist: {
    storage: piniaPluginPersistedstate.sessionStorage(),
  },
  getters: {
    /** 表示用のユーザー名（代理ログイン時は「【代理】」を付加） */
    displayUserName(state): string {
      if (state.loginUser?.proxyLoginMode) {
        return `【代理】${state.loginUser.userName}`;
      }
      else {
        return state.loginUser?.userName ?? '';
      }
    },
  },
  actions: {
    /**
     * ユーザー情報を設定する
     *
     * @param platId PLAT ID
     * @param accountType アカウント区分
     * @param userNameSei ユーザ名（姓）
     * @param userNameMei ユーザ名（名）
     * @param stationAreaDetectionFlag 駅エリア検知フラグ
     * @param permissions 権限機能リスト
     * @param affiliations 所属先リスト
     * @param mustChangePassword パスワード変更必須フラグ
     */
    setUserInfo(
      platId: string,
      accountType: AccountType,
      userNameSei: string,
      userNameMei: string,
      stationAreaDetectionFlag: boolean,
      permissions: Permissions[],
      affiliations: Affiliation[],
      mustChangePassword?: boolean,
    ) {
      this.$patch({
        loginUser: {
          platId,
          accountType,
          userName: joinName(userNameSei, userNameMei),
          stationAreaDetectionFlag,
          permissions,
          affiliations,
          //  初期値は未設定
          workingStation: undefined,
          truck: undefined,
          mustChangePassword: mustChangePassword ?? false,
        },
      });
    },

    /**
     * ユーザー情報を更新する
     *
     * @param accountType アカウント区分
     * @param userNameSei ユーザ名（姓）
     * @param userNameMei ユーザ名（名）
     * @param permissions 権限機能リスト
     * @param stationAreaDetectionFlag 駅エリア検知フラグ
     * @param affiliations 所属先リスト
     */
    updateUserInfo(
      accountType: AccountType,
      userNameSei: string,
      userNameMei: string,
      permissions: Permissions[],
      stationAreaDetectionFlag: boolean,
      affiliations: Affiliation[],
    ) {
      if (this.loginUser) {
        this.loginUser.accountType = accountType;
        this.loginUser.userName = joinName(userNameSei, userNameMei);
        this.loginUser.permissions = permissions;
        this.loginUser.stationAreaDetectionFlag = stationAreaDetectionFlag;
        this.loginUser.affiliations = affiliations;
      }
    },

    /**
     * 作業駅情報を設定する
     *
     * @param stationInfo 作業駅として設定する駅情報
     */
    setWorkingStation(stationInfo: StationWithOrganization) {
      if (this.loginUser) {
        this.loginUser.workingStation = {
          ...stationInfo,
        };
      }
    },

    /**
     * トラック情報を設定する
     *
     * @param workTruckInfo 作業トラック情報
     */
    setTruck(workTruckInfo: WorkTruckInfo) {
      if (this.loginUser) {
        this.loginUser.truck = workTruckInfo;
      }
    },

    /**
     * 代理ログインユーザー情報を設定する
     *
     * @param proxyLoginMode 代理ログインモード
     * @param platId  PLAT ID
     * @param accountType アカウント区分
     * @param userNameSei ユーザ名（姓）
     * @param userNameMei ユーザ名（名）
     * @param permissions 権限機能リスト
     * @param affiliations 所属先リスト
     * @param workingStation 作業駅情報
     */
    setProxyLoginUser(
      proxyLoginMode: ProxyLoginMode,
      platId: string,
      accountType: AccountType,
      userNameSei: string,
      userNameMei: string,
      permissions: Permissions[],
      affiliations: Affiliation[],
      workingStation: StationWithOrganization | undefined,
    ) {
      if (this.loginUser) {
        this.loginUser.proxyLoginMode = proxyLoginMode;
        this.loginUser.originalPlatId = this.loginUser.platId;
        this.loginUser.platId = platId;
        this.loginUser.userName = joinName(userNameSei, userNameMei);
        this.loginUser.accountType = accountType;
        this.loginUser.permissions = permissions;
        this.loginUser.affiliations = affiliations;
        this.loginUser.workingStation = workingStation;
        this.loginUser.truck = undefined;
      }
    },

    /**
     * 代理ログインモードを解除して元のユーザー情報に戻す
     *
     * @param platId 元のPLAT ID
     * @param accountType 元のアカウント区分
     * @param userNameSei 元のユーザ名（姓）
     * @param userNameMei 元のユーザ名（名）
     * @param permissions 元の権限機能リスト
     * @param affiliations 元の所属先リスト
     * @param workingStation 元の作業駅情報
     */
    clearProxyLoginUser(
      platId: string,
      accountType: AccountType,
      userNameSei: string,
      userNameMei: string,
      permissions: Permissions[],
      affiliations: Affiliation[],
      workingStation: StationWithOrganization | undefined,
    ) {
      if (this.loginUser) {
        this.loginUser.proxyLoginMode = undefined;
        this.loginUser.originalPlatId = undefined;
        this.loginUser.platId = platId;
        this.loginUser.userName = joinName(userNameSei, userNameMei);
        this.loginUser.accountType = accountType;
        this.loginUser.permissions = permissions;
        this.loginUser.affiliations = affiliations;
        this.loginUser.workingStation = workingStation;
        this.loginUser.truck = undefined;
      }
    },

    /**
     * 指定した権限を持っているかどうかを判定する
     *
     * @param permission 判定する権限コード
     * @returns true: 権限を持っている false: 権限を持っていない
     */
    hasPermission(permission: Permissions) {
      return this.loginUser?.permissions.includes(permission) ?? false;
    },

    /**
     * ログイン状態を判定する
     *
     * ログインユーザー情報が設定されているかどうかで判定
     *
     * @returns true: ログイン済 false: 未ログイン
     */
    isLoggedIn() {
      return !!this.loginUser;
    },

    /**
     * 申請中かどうかを設定する
     *
     * @param isInApplication 申請中フラグ
     */
    setIsInApplication(isInApplication: boolean) {
      if (this.loginUser) {
        this.loginUser.isInApplication = isInApplication;
      }
    },
  },
});
